import type { Category } from '@/lib/api';

/**
 * How many categories one problem may carry. Mirrors MAX_CATEGORIES_PER_PIN
 * in apps/api/src/lib/categories.ts, which is what actually enforces it —
 * this only shapes the UI.
 */
export const MAX_CATEGORIES = 3;

interface CategoryPickerProps {
  categories: Category[];
  /** Selected slugs in priority order; index 0 is the primary one. */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Distinguishes the two instances' checkboxes in the accessibility tree. */
  idPrefix: string;
  size?: 'normal' | 'compact';
}

/**
 * Ordered multi-select for a pin's categories.
 *
 * Shared by the public submission form and the moderation queue so the two
 * cannot disagree about the cap, the ordering, or what "primary" means — a
 * moderator re-tagging a pin has to see the same rules the submitter saw.
 *
 * Order is selection order, never sorted: the first pick is primary and
 * decides the pin's colour on the map, so the numbered badges are the control's
 * whole point rather than decoration.
 */
export function CategoryPicker({
  categories,
  selected,
  onChange,
  idPrefix,
  size = 'normal',
}: CategoryPickerProps) {
  const toggle = (slug: string) => {
    if (selected.includes(slug)) {
      onChange(selected.filter((s) => s !== slug));
      return;
    }
    if (selected.length >= MAX_CATEGORIES) return;
    onChange([...selected, slug]);
  };

  const padding = size === 'compact' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm';

  return (
    <div className="flex flex-wrap gap-1.5">
      {categories.map((category) => {
        const index = selected.indexOf(category.slug);
        const isOn = index !== -1;
        // Only unselected chips lock at the cap, so an already-picked one
        // stays clickable to undo — a control where nothing responds reads
        // as broken.
        const atLimit = !isOn && selected.length >= MAX_CATEGORIES;

        return (
          <label
            key={category.slug}
            className={`inline-flex items-center gap-1.5 rounded-full border font-medium focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-700 ${padding} ${
              isOn
                ? 'cursor-pointer border-transparent text-white'
                : atLimit
                  ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                  : 'cursor-pointer border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            }`}
            style={isOn ? { backgroundColor: category.color } : undefined}
          >
            <input
              type="checkbox"
              className="sr-only"
              name={`${idPrefix}-categories`}
              checked={isOn}
              disabled={atLimit}
              onChange={() => toggle(category.slug)}
            />
            {isOn && (
              <span
                aria-hidden="true"
                className="flex h-4 w-4 items-center justify-center rounded-full bg-white/25 text-[10px]"
              >
                {index + 1}
              </span>
            )}
            {category.name_cs}
            {/* Spelled out for screen readers: the visual badge is a number
                whose meaning is only obvious once you know the rule. */}
            {isOn && <span className="sr-only">{index === 0 ? ' — hlavní kategorie' : ` — pořadí ${index + 1}`}</span>}
          </label>
        );
      })}
    </div>
  );
}

/** True when two ordered category lists differ — order counts as a change. */
export function categoriesDiffer(a: readonly string[], b: readonly string[]): boolean {
  return a.length !== b.length || a.some((slug, index) => slug !== b[index]);
}
